// frontend/src/contexts/ChecklistContext.js
import React, { createContext, useContext, useState, useCallback } from "react";

const ChecklistContext = createContext();

export const useChecklist = () => {
  const context = useContext(ChecklistContext);
  if (!context) {
    throw new Error("useChecklist must be used within ChecklistProvider");
  }
  return context;
};

export const ChecklistProvider = ({ children }) => {
  const [checklist, setChecklist] = useState(null);
  const [lastFetch, setLastFetch] = useState(null);

  /**
   * Update checklist from backend fetch
   */
  const setChecklistData = useCallback((data) => {
    setChecklist(data);
    setLastFetch(Date.now());
  }, []);

  /**
   * Optimistically mark a requirement as complete
   * @param {string} requirementName - Name of requirement to mark complete
   */
  const markRequirementComplete = useCallback((requirementName) => {
    setChecklist((prev) => {
      if (!prev || !prev.requirements) return prev;

      const updatedRequirements = prev.requirements.map((req) => {
        if (req.name.toLowerCase().includes(requirementName.toLowerCase())) {
          return { ...req, completed: true };
        }
        return req;
      });

      // Recalculate completion percentage
      const totalReqs = updatedRequirements.length;
      const completedReqs = updatedRequirements.filter(
        (r) => r.completed,
      ).length;
      const newPercentage = Math.round((completedReqs / totalReqs) * 100);

      return {
        ...prev,
        requirements: updatedRequirements,
        completionPercentage: newPercentage,
        ready: newPercentage === 100,
      };
    });
  }, []);

  /**
   * Trigger a background refresh without blocking UI
   * @param {Function} fetchFunction - Async function to fetch checklist
   */
  const refreshInBackground = useCallback(
    async (fetchFunction) => {
      try {
        const data = await fetchFunction();
        setChecklistData(data);
      } catch (error) {
      }
    },
    [setChecklistData],
  );

  /**
   * Clear checklist (e.g., on logout)
   */
  const clearChecklist = useCallback(() => {
    setChecklist(null);
    setLastFetch(null);
  }, []);

  const value = {
    checklist,
    setChecklistData,
    markRequirementComplete,
    refreshInBackground,
    clearChecklist,
    lastFetch,
  };

  return (
    <ChecklistContext.Provider value={value}>
      {children}
    </ChecklistContext.Provider>
  );
};
